import * as THREE from 'three';
import type { CasaLeon3DZone } from '../types.js';
import { INTERACTIVE_USER_DATA_KEY } from '../types.js';
import { PALETTE, mat } from './palette.js';
import { brickMaterial, emissiveScreen, grassMaterial, tileMaterial, woodMaterial } from './materials.js';
import { createBrandEntranceSign, createWeatherStation } from './brand-entrance.js';
import { SharedGeometries } from './shared-geometries.js';
import { buildPilotSalonDetail } from './pilot-salon.js';

export interface BuiltWorld {
  root: THREE.Group;
  interactives: THREE.Object3D[];
  zoneFloors: Map<CasaLeon3DZone, THREE.Mesh>;
  tableMeshes: Map<string, THREE.Mesh>;
  grillLightPos: THREE.Vector3;
  brandSign: ReturnType<typeof createBrandEntranceSign>;
  foliage: THREE.Object3D[];
}

function addWall(parent: THREE.Group, x: number, z: number, w: number, d: number, h: number, material: THREE.Material) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  m.position.set(x, h / 2, z);
  m.castShadow = true;
  m.receiveShadow = true;
  parent.add(m);
}

function addFloor(
  parent: THREE.Group,
  x: number,
  z: number,
  w: number,
  d: number,
  material: THREE.Material,
  zone: CasaLeon3DZone,
  zoneFloors: Map<CasaLeon3DZone, THREE.Mesh>,
) {
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, d), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(x, 0.02, z);
  mesh.receiveShadow = true;
  mesh.userData.zone = zone;
  parent.add(mesh);
  zoneFloors.set(zone, mesh);
}

function addPendant(parent: THREE.Group, x: number, z: number, color: number) {
  const cord = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.2, 6), mat(0x222222));
  cord.position.set(x, 2.5, z);
  const shade = new THREE.Mesh(new THREE.ConeGeometry(0.35, 0.4, 12, 1, true), mat(color, { emissive: color, emissiveIntensity: 0.25 }));
  shade.position.set(x, 1.85, z);
  parent.add(cord, shade);
}

function addKitchenBoard(parent: THREE.Group, interactives: THREE.Object3D[]) {
  const frame = new THREE.Mesh(new THREE.BoxGeometry(3.2, 2, 0.14), woodMaterial(2, 0x5c3d2e));
  frame.position.set(10, 1.6, -10.75);
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(2.7, 1.35), emissiveScreen(0x3388ff));
  screen.position.set(10, 1.65, -10.66);
  screen.userData[INTERACTIVE_USER_DATA_KEY] = 'kitchen-board';
  const led = new THREE.Mesh(
    new THREE.TorusGeometry(0.2, 0.03, 8, 24),
    mat(0xff4444, { emissive: 0xff2222, emissiveIntensity: 0.8 }),
  );
  led.rotation.x = Math.PI / 2;
  led.position.set(11.4, 2.35, -10.6);
  parent.add(frame, screen, led);
  interactives.push(screen, frame);
}

function addWhatsAppSign(parent: THREE.Group, interactives: THREE.Object3D[]) {
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 2.4, 10), mat(0x333333, { metalness: 0.5 }));
  post.position.set(2, 1.2, 11);
  const board = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.9, 0.12), mat(0x25d366));
  board.position.set(2, 2.2, 11);
  const logo = new THREE.Mesh(new THREE.CircleGeometry(0.35, 24), mat(0xffffff));
  logo.position.set(2, 2.55, 11.08);
  board.userData[INTERACTIVE_USER_DATA_KEY] = 'whatsapp-sign';
  parent.add(post, board, logo);
  interactives.push(board);
}

function addPhone(parent: THREE.Group, interactives: THREE.Object3D[]) {
  const base = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, 0.35), mat(0x222222, { metalness: 0.4 }));
  base.position.set(-4, 0.84, 12);
  const handset = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.12, 0.5), mat(0x111111, { metalness: 0.5 }));
  handset.position.set(-4, 0.95, 12.05);
  handset.userData[INTERACTIVE_USER_DATA_KEY] = 'phone';
  parent.add(base, handset);
  interactives.push(handset);
}

function addRegister(parent: THREE.Group, interactives: THREE.Object3D[]) {
  const counter = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.95, 0.85), woodMaterial(3, 0x7a5230));
  counter.position.set(-2, 0.48, 12);
  const screen = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.42, 0.04), emissiveScreen(0x44aa88));
  screen.position.set(-2.3, 1.05, 12.35);
  const register = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.25, 0.5), mat(0xcccccc, { metalness: 0.7 }));
  register.position.set(-1.6, 1.02, 12.2);
  register.userData[INTERACTIVE_USER_DATA_KEY] = 'register';
  parent.add(counter, screen, register);
  interactives.push(register, screen);
}

export function buildRestaurantWorld(): BuiltWorld {
  const root = new THREE.Group();
  const interactives: THREE.Object3D[] = [];
  const zoneFloors = new Map<CasaLeon3DZone, THREE.Mesh>();
  const tableMeshes = new Map<string, THREE.Mesh>();
  const wallMat = brickMaterial();
  const wallInner = mat(PALETTE.wall, { roughness: 0.88 });

  addFloor(root, 0, 0, 36, 28, tileMaterial(10), 'entrada', zoneFloors);
  addFloor(root, -8, 2, 14, 16, woodMaterial(6, PALETTE.floorSalon), 'salon', zoneFloors);
  addFloor(root, 10, 2, 12, 16, grassMaterial(), 'patio', zoneFloors);
  addFloor(root, 8, -10, 14, 10, tileMaterial(12), 'cocina', zoneFloors);
  addFloor(root, -2, -10, 8, 10, tileMaterial(8), 'parrilla', zoneFloors);
  addFloor(root, -14, -2, 6, 14, woodMaterial(4, 0x8b5a3c), 'barra', zoneFloors);
  addFloor(root, -2, 12, 8, 6, tileMaterial(6), 'caja', zoneFloors);

  // Sin cielorraso sólido: desde la cámara elevada tapaba todo el interior (CL-VIVO-005B).
  for (const [bx, bz, bw, bd] of [
    [-8, 0, 14, 0.12],
    [10, 0, 12, 0.12],
    [0, 12, 8, 0.1],
  ] as const) {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(bw, 0.08, bd), woodMaterial(2, 0x5c4033));
    beam.position.set(bx, 3.28, bz);
    root.add(beam);
  }

  addWall(root, 0, -14, 38, 0.45, 3.4, wallMat);
  addWall(root, -18, 0, 0.45, 30, 3.4, wallMat);
  addWall(root, 18, 0, 0.45, 30, 3.4, wallMat);
  addWall(root, 4, 14, 28, 0.45, 3.4, wallMat);
  addWall(root, -10, 8, 0.35, 10, 2.6, wallInner);
  addWall(root, 4, 8, 0.35, 10, 2.6, wallInner);

  for (let i = -5; i <= 5; i += 2.5) {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(11.5, 0.12, 0.18), woodMaterial(2));
    beam.position.set(10 + i * 0.15, 2.85, 2);
    root.add(beam);
  }
  for (let i = -5; i <= 5; i += 2.5) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.16, 2.9, 10), woodMaterial(2, 0x6b4423));
    post.position.set(10 + i, 1.45, -5);
    root.add(post);
    const post2 = post.clone();
    post2.position.z = 9;
    root.add(post2);
  }
  for (let x = 6; x <= 14; x += 2) {
    for (let z = -3; z <= 7; z += 2.5) {
      addPendant(root, x, z, 0xffcc88);
    }
  }

  const hood = new THREE.Mesh(new THREE.BoxGeometry(3.5, 0.35, 2.2), mat(0x888888, { metalness: 0.75, roughness: 0.35 }));
  hood.position.set(-2, 2.8, -10);
  const grillBody = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.95, 1.3), mat(PALETTE.metal, { metalness: 0.65 }));
  grillBody.position.set(-2, 0.92, -10);
  const fire = new THREE.Mesh(
    new THREE.BoxGeometry(2.4, 0.12, 0.9),
    mat(PALETTE.emissiveWarm, { emissive: PALETTE.emissiveWarm, emissiveIntensity: 1.1 }),
  );
  fire.position.set(-2, 1.42, -10);
  root.add(hood, grillBody, fire);

  const barTop = new THREE.Mesh(new THREE.BoxGeometry(5.2, 1.08, 0.75), woodMaterial(3, 0xa07040));
  barTop.position.set(-14, 0.56, -2);
  const barBack = new THREE.Mesh(new THREE.BoxGeometry(5.2, 2.4, 0.35), mat(PALETTE.brand, { roughness: 0.55 }));
  barBack.position.set(-14, 1.85, -5.1);
  const mirror = new THREE.Mesh(
    new THREE.PlaneGeometry(4.5, 1.2),
    mat(0x99bbcc, { metalness: 0.9, roughness: 0.15 }),
  );
  mirror.position.set(-14, 2.2, -4.88);
  root.add(barTop, barBack, mirror);
  for (let i = 0; i < 8; i++) {
    const bottle = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 0.45, 8), mat(0x226622 + i * 0x050500));
    bottle.position.set(-15.5 + i * 0.35, 1.35, -5.05);
    root.add(bottle);
  }

  const pass = new THREE.Mesh(new THREE.BoxGeometry(4, 0.9, 0.6), mat(0xeeeeee, { metalness: 0.3 }));
  pass.position.set(8, 0.9, -8);
  root.add(pass);

  // Mesas din\u00e1micas: capa 3D separada (CL-VIVO-006B). Sin etiquetas flotantes.

  addKitchenBoard(root, interactives);
  addWhatsAppSign(root, interactives);
  addPhone(root, interactives);
  addRegister(root, interactives);

  const foliage: THREE.Object3D[] = [];
  const plants = [[-16, 10], [16, 8], [16, -8], [-16, -8], [0, -12], [12, 10]] as const;
  const potMat = mat(0x5c4033);
  const bushMat = mat(0x3d6b3d);
  const potMesh = new THREE.InstancedMesh(SharedGeometries.pot(), potMat, plants.length);
  const bushMesh = new THREE.InstancedMesh(SharedGeometries.bush(), bushMat, plants.length);
  potMesh.castShadow = true;
  bushMesh.castShadow = true;
  const m = new THREE.Matrix4();
  plants.forEach(([px, pz], i) => {
    m.makeTranslation(px, 0.28, pz);
    potMesh.setMatrixAt(i, m);
    m.makeTranslation(px, 0.9, pz);
    bushMesh.setMatrixAt(i, m);
  });
  foliage.push(bushMesh);
  potMesh.instanceMatrix.needsUpdate = true;
  bushMesh.instanceMatrix.needsUpdate = true;
  root.add(potMesh, bushMesh);

  buildPilotSalonDetail(root);

  const brandSign = createBrandEntranceSign();
  root.add(brandSign.group);
  root.add(createWeatherStation(interactives));

  const rug = new THREE.Mesh(new THREE.CircleGeometry(2.2, 32), mat(0x8b1e1e, { roughness: 1 }));
  rug.rotation.x = -Math.PI / 2;
  rug.position.set(0, 0.03, 10);
  root.add(rug);

  return {
    root,
    interactives,
    zoneFloors,
    tableMeshes,
    grillLightPos: new THREE.Vector3(-2, 1.5, -10),
    brandSign,
    foliage,
  };
}
